/**
 * The incident state machine, on the client — WP-L5 (Task 53).
 *
 * This is the module the acceptance criterion rests on: *"UI không cho transition
 * không hợp lệ."* It is treated as a security control rather than a nicety, so three
 * things about it are deliberate and each is asserted somewhere.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  1. THE TABLE IS THE SERVER'S, AND A TEST PROVES IT CANNOT DRIFT
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `TRANSITIONS` below is a transcription of `TRANSITIONS` in
 * `src/modules/incidents/state-machine.ts`, and `state-machine.test.ts` **parses that
 * file at test time** and compares the two as sets, in both directions, per state.
 * An edge added, removed or re-pointed on the server turns this project red. That is
 * the technique `web/src/api/error-safety.test.ts` already uses against
 * `docs/API_CONTRACT.md` §6, one level up from a file list: scope the guard to the
 * property — "the client offers exactly the moves the server accepts" — rather than
 * to a list somebody remembers to update.
 *
 * **Why not import the server module?** It has no imports of its own, so it would
 * resolve. But this bundle is static JavaScript served to browsers, and a
 * `web/ → src/modules/` import is a precedent whose second use brings a repository,
 * a `pg` handle or a secret along with it. The set comparison buys the same
 * no-drift guarantee at test time and keeps the boundary.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  2. ABSENT, NOT DISABLED
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * The screen `map`s over `allowedTransitions(state)`. It does **not** walk all eight
 * states computing a `disabled` flag, because that shape ships a `disabled` button
 * an operator re-enables in devtools and presses — and because a greyed control
 * asserts "the system will not let you" about a move that does not exist at all.
 * The illegal targets have **no DOM node**, which is what `incidents.test.tsx`
 * asserts by querying the container for every one of the eight states.
 *
 * `closed`'s row is `[]` — an empty list, not an absent key — so a closed incident
 * renders no controls and a sentence, rather than an empty toolbar.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  3. THE SERVER STILL REFUSES, AND THE REFUSAL IS STILL RENDERED
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * A control nobody can reach is not a reason to swallow the refusal behind it. A
 * stale screen whose incident moved underneath it can still issue a move the server
 * refuses `409 WORKFORCE_INVALID_STATE_TRANSITION`, carrying `from_state`, `to_state`
 * and `allowed_transitions[]`. The screen renders the server's own sentence and the
 * server's own list. This module never gets to decide that an issued move was fine.
 */

/** Master §15.3's eight states, in Master's own order. */
export const INCIDENT_STATES = [
  'new',
  'acknowledged',
  'investigating',
  'waiting_for_employee',
  'resolved',
  'false_positive',
  'escalated',
  'closed',
] as const;

export type IncidentState = (typeof INCIDENT_STATES)[number];

export function isIncidentState(value: unknown): value is IncidentState {
  return typeof value === 'string' && (INCIDENT_STATES as readonly string[]).includes(value);
}

/**
 * The whole gate. Every state has a row, `closed` included, whose row is empty.
 *
 * An absent key and an empty list are different statements and only one of them
 * survives a refactor — the server's own file says so, and this copy keeps the
 * property so the set comparison in the test compares like with like.
 */
export const TRANSITIONS: Readonly<Record<IncidentState, readonly IncidentState[]>> = {
  new: ['acknowledged', 'investigating', 'escalated', 'false_positive'],
  acknowledged: [
    'investigating',
    'waiting_for_employee',
    'escalated',
    'resolved',
    'false_positive',
  ],
  investigating: ['waiting_for_employee', 'escalated', 'resolved', 'false_positive'],
  waiting_for_employee: ['investigating', 'escalated', 'resolved', 'false_positive'],
  escalated: ['investigating', 'waiting_for_employee', 'resolved', 'false_positive'],
  resolved: ['closed', 'investigating'],
  false_positive: ['closed', 'investigating'],
  closed: [],
};

/**
 * The three states `wp_incidents_resolve_ck` refuses to admit a row into without a
 * `resolution`, a `resolution_code` and a `resolved_at`.
 *
 * The API's half of the same rule is a `400` naming the `resolution` field, so the
 * form asks for one **before** sending rather than after being refused: the
 * constraint is the authority, and learning a mandatory field from a `400` is
 * learning it from the wrong end.
 */
export const CONCLUDING_STATES: readonly IncidentState[] = ['resolved', 'false_positive', 'closed'];

export function isConcludingState(state: IncidentState): boolean {
  return CONCLUDING_STATES.includes(state);
}

/** The only state a `resolved` or `false_positive` incident can be reopened into. */
export const REOPEN_STATE: IncidentState = 'investigating';

/**
 * Is this edge a reopen?
 *
 * Used to attach the sentence that a reopen **clears the conclusion and keeps the
 * original deadline**. Both halves are counter-intuitive, and a screen that offered
 * the move without saying either would be offering a button whose effect the
 * operator has to have read a report to know.
 */
export function isReopen(from: IncidentState, to: IncidentState): boolean {
  return (from === 'resolved' || from === 'false_positive') && to === REOPEN_STATE;
}

/** `true` for exactly the state with no outgoing edge. Derived, never listed. */
export function isTerminal(state: IncidentState): boolean {
  return TRANSITIONS[state].length === 0;
}

export function allowedTransitions(from: IncidentState): readonly IncidentState[] {
  return TRANSITIONS[from];
}

export function canTransition(from: IncidentState, to: IncidentState): boolean {
  return TRANSITIONS[from].includes(to);
}

/**
 * The moves this state does **not** allow — computed as the complement, so it is
 * impossible for a state to be missing from both lists.
 *
 * Exists for the tests, and for nothing else: no component renders it, because
 * rendering it is exactly the `disabled`-button shape §2 above rejects. It is
 * exported so `incidents.test.tsx` can assert absence over a set it did not itself
 * write down — a hand-written list of illegal moves in a test is a second table that
 * drifts, and it would drift towards agreeing with a broken implementation.
 */
export function forbiddenTransitions(from: IncidentState): readonly IncidentState[] {
  const allowed = new Set<IncidentState>(TRANSITIONS[from]);
  return INCIDENT_STATES.filter((state) => !allowed.has(state));
}
